import { useMemo, useState } from 'react';
import { Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import { AutoPackageList } from '../package/AutoPackageList';
import { DimensionAndWeightDisplay, DimensionDisplay } from './DimensionDisplay';
import { DimensionsAreValid } from './panels/DimensionsAreValid';
import GetWeightOptions from './GetWeightOptions';
import {
  P2GBox,
  P2GButton,
  P2GFade,
  P2GGrid,
  P2GLink,
  P2GLinkGroup,
  P2GTextField,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useMeasurementSystem } from '~/hooks/use-measurement-system';
import {
  LocalUnitsEntry,
  ParcelUnitField,
  applyPresetToLocalUnitsEntry,
  clearLocalUnitsEntryFields,
  kgToLbDisplay,
  localUnitsEntryHasValues,
  parcelEntryToMetric,
  resolveLimitDimension,
  resolveLimitWeight,
  resolveParcelDisplay
} from '~/apps/smartsend/lib/helpers/DimensionUnits';

interface props {
  onSave?: (dimensions: Dimensions & { Name?: string }, localUnitsEntry?: LocalUnitsEntry) => void;
  onUpdate?: (dimensions: Dimensions & { Name?: string }, localUnitsEntry?: LocalUnitsEntry) => void;
  dimensions: Dimensions;
  localUnitsEntry?: LocalUnitsEntry;
  startMinimised: boolean;
  fallback?: Dimensions;
  dimensionsRequired?: boolean;
  getPackages?: () => Promise<PackageApiModel[]>;
  hideDimensionsIfNotRequired?: boolean;
  hideWeight?: boolean;
  showWhenTouched?: boolean;
  children?: React.ReactNode;
}

const fieldKey: Record<ParcelUnitField, keyof Dimensions> = {
  weight: 'Weight',
  length: 'Length',
  width: 'Width',
  height: 'Height'
};

const CollapsedView = (props: { dimensions: Dimensions; localUnitsEntry?: LocalUnitsEntry; onEdit: () => void }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GBox mt={1}>
      <P2GGrid container spacing={2} alignItems="center" alignContent="center">
        <P2GGrid item flexShrink={1}>
          <DimensionAndWeightDisplay
            variant="body2"
            dimensions={props.dimensions}
            localUnitsEntry={props.localUnitsEntry}
          />
        </P2GGrid>
        <P2GGrid item flexShrink={1}>
          <P2GLink onClick={props.onEdit}>{t('EDIT_LINK', { defaultValue: 'Edit' })}</P2GLink>
        </P2GGrid>
      </P2GGrid>
    </P2GBox>
  );
};

export const DimensionsSelection: React.FC<props> = (props) => {
  const [touched, setTouched] = useState(false);
  const [staging, setStaging] = useState<Dimensions>({ ...props.dimensions });
  const [localUnitsEntry, setLocalUnitsEntry] = useState<LocalUnitsEntry>({ ...props.localUnitsEntry });
  const [expand, setExpand] = useState(!props.startMinimised);
  const isXs = useP2GMediaQueryOnly('xs');
  const { t } = useTranslation([TranslationNamespaces.SMARTSEND, TranslationNamespaces.COMMON]);
  const { unit } = useMeasurementSystem();
  const imperial = unit === 'imperial';

  const weightOptionsMetric = useMemo(
    () => GetWeightOptions(isXs || props.showWhenTouched),
    [isXs, props.showWhenTouched]
  );
  const weightOptions = useMemo(
    () => (imperial ? weightOptionsMetric.map((w) => kgToLbDisplay(w)) : weightOptionsMetric),
    [imperial, weightOptionsMetric]
  );

  const showDims = useMemo(() => {
    if (props.hideDimensionsIfNotRequired && !props.dimensionsRequired) {
      return false;
    }
    return true;
  }, [props.hideDimensionsIfNotRequired, props.dimensionsRequired]);

  const onUpdate = (dims: Dimensions & { Name?: string }, updatedLocalUnitsEntry: LocalUnitsEntry) => {
    setTouched(true);
    setStaging(dims);
    setLocalUnitsEntry(updatedLocalUnitsEntry);
    if (props.onUpdate) {
      props.onUpdate(dims, updatedLocalUnitsEntry);
    }
  };

  const onFieldChange = (field: ParcelUnitField, raw: string) => {
    const { metricValue, localUnitsEntry: updatedLocalUnitsEntry } = parcelEntryToMetric(
      field,
      raw,
      unit,
      localUnitsEntry
    );
    onUpdate({ ...staging, [fieldKey[field]]: metricValue }, updatedLocalUnitsEntry);
  };

  // The displayed chip value is the metric preset converted+rounded for display (e.g. 0.75kg ->
  // 1.7lb) - re-converting that rounded lb value back to kg would drift the stored weight (~0.77kg).
  // Always store the exact metric preset; only the entry records the lb value the user saw/clicked.
  const onWeightQuickSelect = (index: number) => {
    const metricWeight = weightOptionsMetric[index];
    if (imperial) {
      onUpdate(
        { ...staging, Weight: metricWeight },
        { ...localUnitsEntry, unitSystem: 'imperial', originalWeightLb: weightOptions[index] }
      );
    } else {
      onUpdate({ ...staging, Weight: metricWeight }, clearLocalUnitsEntryFields(localUnitsEntry, ['weight']));
    }
  };

  // Presets/fallback selections set metric values directly (not typed by the user). In metric this
  // just clears the as-entered entry for the fields provided; in imperial it records the rounded
  // value the user was shown for those fields (matching AutoPackageList's own display conversion),
  // so it reads as an as-entered imperial value rather than being silently re-derived every render.
  const onMetricPreset = (dims: Dimensions & { Name?: string }, providedFields: ParcelUnitField[]) => {
    const metricValues: Partial<Record<ParcelUnitField, number>> = {};
    providedFields.forEach((field) => {
      metricValues[field] = dims[fieldKey[field]];
    });
    onUpdate(dims, applyPresetToLocalUnitsEntry(metricValues, unit, localUnitsEntry, providedFields));
  };

  const weightDisplay = (w: number) => {
    if (imperial) {
      return `${w} lb`;
    }
    return w < 1 ? `${w * 1000}g` : `${w}kg`;
  };

  if (!expand) {
    return (
      <CollapsedView
        dimensions={props.dimensions}
        localUnitsEntry={props.localUnitsEntry}
        onEdit={() => setExpand(true)}
      />
    );
  }

  const weightValue = resolveParcelDisplay('weight', staging.Weight ?? 0, unit, localUnitsEntry);
  const lengthValue = resolveParcelDisplay('length', staging.Length ?? 0, unit, localUnitsEntry);
  const widthValue = resolveParcelDisplay('width', staging.Width ?? 0, unit, localUnitsEntry);
  const heightValue = resolveParcelDisplay('height', staging.Height ?? 0, unit, localUnitsEntry);
  const weightMax = resolveLimitWeight(2500, unit).value;
  const dimensionMax = resolveLimitDimension(300, unit).value;

  return (
    <P2GBox>
      {!props.hideWeight && (
        <P2GGrid container spacing={2} alignItems="center">
          <P2GGrid item sm={4} xs={12}>
            <P2GTextField
              fullWidth
              label={t('WEIGHT_INPUT_LABEL', { defaultValue: 'Weight' })}
              type="number"
              required
              showErrorsWhenPristine={true}
              value={(staging.Weight != null ? weightValue.value : '').toString()}
              onChange={(v) => onFieldChange('weight', v.currentTarget.value)}
              endAdornment={weightValue.unit}
              step={imperial ? 0.1 : 0.01}
              max={weightMax}
              min={imperial ? 0.1 : 0.0}
            />
          </P2GGrid>
          <P2GGrid item sm={8} xs={12}>
            <P2GLinkGroup
              links={weightOptions.map((w, idx) => (
                <P2GLink variant="body2" key={w} onClick={() => onWeightQuickSelect(idx)}>
                  {w == weightValue.value ? <strong>{weightDisplay(w)}</strong> : weightDisplay(w)}
                </P2GLink>
              ))}
            />
          </P2GGrid>
        </P2GGrid>
      )}
      {showDims && (
        <P2GBox pt={props.hideWeight ? 0 : 2}>
          <AutoPackageList
            selected={staging}
            packages={props.getPackages}
            onSelect={(p) =>
              onMetricPreset(
                { ...p, Weight: p.Weight || staging.Weight },
                p.Weight ? ['length', 'width', 'height', 'weight'] : ['length', 'width', 'height']
              )
            }
          >
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('LENGTH_INPUT_LABEL', { defaultValue: 'Length' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Length != null ? lengthValue.value : '').toString()}
                onChange={(v) => onFieldChange('length', v.currentTarget.value)}
                endAdornment={lengthValue.unit}
                step={1}
                max={dimensionMax}
                min={imperial && props.dimensionsRequired ? 1 : undefined}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('WIDTH_INPUT_LABEL', { defaultValue: 'Width' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Width != null ? widthValue.value : '').toString()}
                onChange={(v) => onFieldChange('width', v.currentTarget.value)}
                endAdornment={widthValue.unit}
                step={1}
                max={dimensionMax}
                min={imperial && props.dimensionsRequired ? 1 : undefined}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
            <P2GGrid item xs={4}>
              <P2GTextField
                fullWidth
                label={t('HEIGHT_INPUT_LABEL', { defaultValue: 'Height' })}
                type="number"
                required={!!props.dimensionsRequired}
                value={(staging.Height != null ? heightValue.value : '').toString()}
                onChange={(v) => onFieldChange('height', v.currentTarget.value)}
                endAdornment={heightValue.unit}
                step={1}
                max={dimensionMax}
                min={imperial && props.dimensionsRequired ? 1 : undefined}
                showErrorsWhenPristine={true}
              />
            </P2GGrid>
          </AutoPackageList>
        </P2GBox>
      )}
      {props.fallback && props.fallback.Height > 0 && props.fallback.Width > 0 && props.fallback.Length > 0 && (
        <P2GLinkGroup
          pt={1}
          links={[
            <P2GLink
              onClick={() =>
                onMetricPreset({ ...props.fallback, Weight: staging.Weight }, ['length', 'width', 'height'])
              }
            >
              <Trans
                t={t}
                i18nKey="SET_AS_FALLBACK_LINK"
                values={{
                  dimension: DimensionDisplay(props.fallback)
                }}
                defaults="Set as {{dimension}}"
              />
            </P2GLink>
          ]}
        />
      )}
      {props.onSave && (!props.showWhenTouched || touched) && (
        <P2GBox mt={2}>
          <P2GFade in timeout={1000}>
            <P2GButton
              disabled={!DimensionsAreValid(staging, props.dimensionsRequired)}
              onClick={() =>
                props.onSave(staging, localUnitsEntryHasValues(localUnitsEntry) ? localUnitsEntry : undefined)
              }
              variant="primary"
            >
              {t('SAVE_DIMENSIONS_BUTTON', { defaultValue: 'Save Dimensions' })}
            </P2GButton>
          </P2GFade>
        </P2GBox>
      )}
    </P2GBox>
  );
};
